import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Form, Modal, Skeleton } from 'antd'
import { useSelector } from 'react-redux'
import {
  getOrdersFormAlert,
  getOrdersFormData,
  getOrdersFormError,
  getOrdersFormLoading,
  getVisibleFormData,
} from '../../selectors'
import { useDebounce } from 'src/react-app/hooks/useDebounce/useDebounce'
import { HStack, VStack } from '../../../../../ui/Stack'
import { DefaultJsonForm } from '../../../../../components/v2/default-json-form'
import { useSaveResult } from '../../hooks/useSaveResult'
import { useNavigate } from 'react-router-dom'
import { isEqual, cloneDeep } from 'lodash'
import { useAppDispatch } from '../../../../../hooks/useAppDispatch/useAppDispatch'

interface OrdersFormsProps {
  className?: string
  id: string | number
}

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 24 },
  },
}

export const Forms: React.FC<OrdersFormsProps> = memo((props: OrdersFormsProps) => {
  const { id } = props
  const [form] = Form.useForm()
  const dispatch = useAppDispatch()
  const ordersFormData = useSelector(getOrdersFormData)
  const ordersFormIsLoading = useSelector(getOrdersFormLoading)
  const ordersFormError = useSelector(getOrdersFormError)
  const ordersFormAlert = useSelector(getOrdersFormAlert)
  const visibleFormData = useSelector(getVisibleFormData)
  const { saveResult } = useSaveResult()
  const navigate = useNavigate()
  const formRef = useRef<typeof visibleFormData>()
  const [prevFormData, setPrevFormData] = useState<any>(undefined)

  useEffect(() => {
    if (visibleFormData && ordersFormData) {
      formRef.current = visibleFormData
      setPrevFormData(cloneDeep(visibleFormData.resultObject))
    }
  }, [visibleFormData, ordersFormData])

  const saveCase = useCallback(
    async (isNextStage = false, stageCode?: string) => {
      await saveResult(
        id,
        formRef.current,
        ordersFormData?.stages?.current,
        false,
        () => {},
        isNextStage,
        stageCode,
      )
    },
    [id, saveResult, ordersFormData],
  )

  useEffect(() => {
    const redirectUrl = ordersFormData?.redirectUrl
    if (redirectUrl) {
      navigate(redirectUrl)
    }
  }, [ordersFormData])

  const debounceSaveCase = useDebounce(saveCase, 1000)

  const onChangeForm = useCallback(
    async ({ formData }) => {
      if (!formRef.current) {
        return
      }
      // проверяет, являются ли данные идентичными
      if (isEqual(prevFormData, formData)) {
        return
      }
      // если инициализация - заполняет промежуточное состояние
      if (!prevFormData && !isEqual(prevFormData, formData)) {
        setPrevFormData({ ...formData })
        return
      }

      try {
        await debounceSaveCase()
      } finally {
        setPrevFormData({ ...formData })
        formRef.current.resultObject = formData
      }
    },
    [formRef, debounceSaveCase, prevFormData],
  )

  useEffect(() => {
    return () => {
      setPrevFormData(undefined)
    }
  }, [])

  useEffect(() => {
    if (ordersFormAlert) {
      Modal.warning({
        title: 'Предупреждение',
        content: ordersFormAlert,
        onOk: () => {
          dispatch({ type: 'ORDERS_FORM_ALERT', payload: null })
        },
      })
    }
  }, [ordersFormAlert, dispatch])

  if (ordersFormError && !ordersFormIsLoading && !ordersFormData) {
    return (
      <HStack gap={'8'} className={'mapWrapper'}>
        При выполнении запроса возникла ошибка. Попробуйте перезагрузить страницу или открыть
        карточку повторно.
      </HStack>
    )
  }

  return (
    <>
      <HStack gap={'8'} max>
        <Form
          {...formItemLayout}
          layout="horizontal"
          form={form}
          scrollToFirstError={true}
          style={{ width: '100%' }}
          component="div"
        >
          {ordersFormError && !ordersFormIsLoading ? (
            <div className={'alertWrapper'}>
              <Alert
                message="При выполнении операции возникла ошибка:"
                showIcon
                type="error"
                description={ordersFormError}
              />
            </div>
          ) : null}
          {!ordersFormIsLoading ? (
            <VStack gap={'16'} max align={'stretch'} className={'ordersForm'}>
              {visibleFormData && (
                <DefaultJsonForm
                  formData={visibleFormData.resultObject}
                  schema={visibleFormData.resultSchema}
                  onChange={onChangeForm}
                  orientation="horizontal"
                />
              )}
            </VStack>
          ) : (
            <Skeleton active />
          )}
        </Form>
      </HStack>
    </>
  )
})

Forms.displayName = 'OrderForm'
